import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';

export interface Match {
  id: number;
  tipo_conteudo: 'licitacao' | 'noticia';
  conteudo_id: number;
  grupo_id: number;
  score: number;
  status: 'pendente' | 'aprovado' | 'rejeitado' | 'enviado';
  motivo: string | null;
  revisado_por: number | null;
  revisado_at: string | null;
  enviado_at: string | null;
  created_at: string;
  grupo_nome: string;
  conteudo: {
    id: number;
    titulo: string;
    orgao?: string;
    uf?: string;
    valor_estimado?: number;
    fonte?: string;
  };
}

interface MatchesResponse {
  success: boolean;
  data: {
    matches: Match[];
    total: number;
  };
}

interface StatsResponse {
  success: boolean;
  data: {
    pendentes: number;
    aprovados: number;
    rejeitados: number;
    enviados: number;
    total_licitacoes: number;
    total_noticias: number;
  };
}

export function useMatchingStats() {
  return useQuery({
    queryKey: ['matching-stats'],
    queryFn: async () => {
      const response = await api.get<StatsResponse>('/matching/stats');
      return response.data;
    }
  });
}

export function usePendingMatches(limit = 50, offset = 0) {
  return useQuery({
    queryKey: ['pending-matches', limit, offset],
    queryFn: async () => {
      const response = await api.get<MatchesResponse>(`/matching/pendentes?limit=${limit}&offset=${offset}`);
      return response.data;
    }
  });
}

export function useUpdateMatchStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ matchId, status, motivo }: { matchId: number; status: 'aprovado' | 'rejeitado'; motivo?: string }) => {
      const response = await api.put<{ success: boolean; data: { match: Match } }>(`/matching/${matchId}/status`, { status, motivo });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-matches'] });
      queryClient.invalidateQueries({ queryKey: ['matching-stats'] });
    }
  });
}

export function useRunMatching() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params?: { tipo?: string; limit?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.tipo) searchParams.append('tipo', params.tipo);
      if (params?.limit) searchParams.append('limit', String(params.limit));
      const response = await api.post<{ success: boolean; data: { results: { licitacoes: { processed: number; matched: number }; noticias: { processed: number; matched: number } }; duration_ms: number } }>(`/matching/run?${searchParams.toString()}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-matches'] });
      queryClient.invalidateQueries({ queryKey: ['matching-stats'] });
    }
  });
}
